import { apiClient } from './api/apiClient';
import {
  DeploymentListResponse,
  DeploymentDetailResponse,
  DeploymentStatsResponse,
  DeploymentResult,
  ActiveDeploymentResponse,
  TriggerDeploymentRequest,
  TriggerDeploymentResponse,
  LastVersionResponse,
  ManageAppname,
} from '../models/Deployment';

class DeploymentService {
  async getDeployments(projectId: string): Promise<DeploymentListResponse> {
    try {
      const response = await apiClient.get(`/v0.1/parking/${projectId}/deployments`);
      return response.data;
    } catch (error) {
      console.error('배포 결과 목록 조회 실패:', error);
      throw error;
    }
  }

  async getDeploymentById(projectId: string, deploymentId: number): Promise<DeploymentDetailResponse> {
    try {
      const response = await apiClient.get(`/v0.1/parking/${projectId}/deployments/${deploymentId}`);
      return response.data;
    } catch (error) {
      console.error('배포 결과 상세 조회 실패:', error);
      throw error;
    }
  }

  async getDeploymentStats(projectId: string): Promise<DeploymentStatsResponse> {
    try {
      const response = await apiClient.get(`/v0.1/parking/${projectId}/deployments/stats`);
      return response.data;
    } catch (error) {
      console.error('배포 통계 조회 실패:', error);
      throw error;
    }
  }

  async createDeployment(projectId: string, deployment: Partial<DeploymentResult>): Promise<DeploymentDetailResponse> {
    try {
      const response = await apiClient.post(`/v0.1/parking/${projectId}/deployments`, deployment);
      return response.data;
    } catch (error) {
      console.error('배포 결과 생성 실패:', error);
      throw error;
    }
  }

  async updateDeployment(projectId: string, deploymentId: number, deployment: Partial<DeploymentResult>): Promise<DeploymentDetailResponse> {
    try {
      const response = await apiClient.put(`/v0.1/parking/${projectId}/deployments/${deploymentId}`, deployment);
      return response.data;
    } catch (error) {
      console.error('배포 결과 수정 실패:', error);
      throw error;
    }
  }

  async deleteDeployment(projectId: string, deploymentId: number): Promise<void> {
    try {
      await apiClient.delete(`/v0.1/parking/${projectId}/deployments/${deploymentId}`);
    } catch (error) {
      console.error('배포 결과 삭제 실패:', error);
      throw error;
    }
  }

  // [Assumed Contract — site-management-ui-revamp]
  // 현재 구동 중(활성) 현장 조회. null 반환 시 구동 중인 현장 없음.
  // TODO(backend-contract): 실제 엔드포인트 확정 후 URL만 교체.
  async getActiveDeployment(): Promise<ActiveDeploymentResponse> {
    try {
      const response = await apiClient.get('/v0.1/deployments/active');
      return response.data;
    } catch (error) {
      console.error('활성 배포 조회 실패:', error);
      throw error;
    }
  }

  // [Assumed Contract — site-management-ui-revamp]
  // 특정 현장의 최신 배포 상태 조회. 폴링 용도.
  async getLatestDeployment(projectId: string): Promise<DeploymentDetailResponse> {
    try {
      const response = await apiClient.get(`/v0.1/parking/${projectId}/deployments/latest`);
      return response.data;
    } catch (error) {
      console.error('최신 배포 조회 실패:', error);
      throw error;
    }
  }

  // [Assumed Contract — site-management-ui-revamp]
  // 배포 트리거 (GitHub Actions workflow_dispatch 연동). 브랜치는 현재 'develop' 고정.
  // 단일 활성 제약은 백엔드가 보장한다고 가정 — 동일 시점 다른 현장 구동 시 롤링 교체.
  async triggerDeployment(req: TriggerDeploymentRequest): Promise<TriggerDeploymentResponse> {
    try {
      const response = await apiClient.post('/v0.1/deployments/trigger', req);
      return response.data;
    } catch (error) {
      console.error('배포 트리거 실패:', error);
      throw error;
    }
  }

  // manage-deploy-versioning: 마지막 SemVer git tag 조회 + minor +1 추천 버전.
  // smartparking-manage / smartparking-manage-backend 만 지원.
  // 백엔드는 GitHub API rate limit 대비 30s in-memory 캐시 사용.
  async getLastVersion(appname: ManageAppname): Promise<LastVersionResponse> {
    try {
      const response = await apiClient.get('/v0.1/deployments/last-version', {
        params: { appname },
      });
      return response.data;
    } catch (error) {
      console.error('마지막 버전 조회 실패:', error);
      throw error;
    }
  }
}

const deploymentService = new DeploymentService();
export default deploymentService;